import React from 'react';
import { ChevronDown } from 'lucide-react';
import {
  useOutputLanguage,
  OUTPUT_LANGUAGES,
} from '../hooks/useOutputLanguage';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Button } from './ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

interface OutputLanguageSelectProps {
  disabled?: boolean;
  variant?: 'button' | 'compact' | 'dictation';
}

const OutputLanguageSelect: React.FC<OutputLanguageSelectProps> = ({
  disabled = false,
  variant = 'button',
}) => {
  const { outputLanguage, loading, updateOutputLanguage } = useOutputLanguage();

  const handleLanguageSelect = async (language: string) => {
    await updateOutputLanguage(language);
  };

  // Find current language object
  const currentLanguage =
    OUTPUT_LANGUAGES.find((lang) => lang.code === outputLanguage) ||
    OUTPUT_LANGUAGES[0];

  // Render dictation-style select (matches mode dropdown)
  if (variant === 'dictation') {
    return (
      <Select
        value={outputLanguage}
        onValueChange={handleLanguageSelect}
        disabled={disabled || loading}
      >
        <SelectTrigger className="min-w-[120px] max-w-[160px] h-10">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {OUTPUT_LANGUAGES.map((lang) => (
            <SelectItem key={lang.code} value={lang.code} className="py-2">
              {lang.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  // Render button-style dropdown (consistent with language pattern)
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant={variant === 'compact' ? 'ghost' : 'minimal'}
          className={
            variant === 'compact'
              ? 'h-8 text-black hover:text-black'
              : 'w-fit text-black hover:text-black'
          }
          disabled={disabled || loading}
          title="Select output language"
        >
          <span>{currentLanguage.name}</span>
          <ChevronDown className="h-4 w-4 ml-1" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[140px] max-w-[160px]">
        {OUTPUT_LANGUAGES.map((lang) => {
          const isSelected = lang.code === outputLanguage;
          return (
            <DropdownMenuItem
              key={lang.code}
              onClick={() => handleLanguageSelect(lang.code)}
            >
              <span className="flex-1 truncate" title={lang.name}>
                {lang.name}
              </span>
              {isSelected && (
                <div className="w-2 h-2 bg-primary rounded-full ml-2" />
              )}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default OutputLanguageSelect;
